import Payment from "payment";
import { SuitLevelV2Inventory } from "../store/data/types";
import { CartManagerState } from "../store/data/purchaseSeats/cartManager";
import { TeamConfig } from "../hooks/useTeamConfig";
import { globalConfig } from "../config/global_config";
import { format } from "date-fns";
import i18n from "i18next";

export function getElementHeightById(id: string) {
  let element = document.getElementById(id);
  return element?.offsetHeight;
}

/**
 * Parses the error message from an Axios error object.
 * @param error - The Axios error object.
 * @returns The error message string, or undefined if not found.
 */
export const parseErrorMessage = (error: any) => {
  const locale = i18n.language;
  const fallbackMessage = {
    en: "Something went wrong. Please try again or contact support if the error persists.",
    fr: "Une erreur est survenue. Veuillez réessayer ou contacter le support si l'erreur persiste.",
  };

  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    error?.response?.data ||
    fallbackMessage[locale as keyof typeof fallbackMessage] ||
    fallbackMessage.en
  );
};

export function parseApiErrorMessage(error: any) {
  return parseErrorMessage(error);
}

/**
 * Debounce function to add delay between actions
 * @returns function that should be called with callback and delay
 */
export function debounce() {
  let timeout: any;
  return function (callback: () => void, delay: number) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback();
    }, delay);
  };
}

/**
 * Extract url params into javascript object
 * @param url string
 * @returns javascript object containing the params and value
 */
export function extractUrlParams(url: string) {
  let result = Object.create(null);
  let paramsStr = url.split("?")[1];
  if (!paramsStr) return;

  const paramsArray = paramsStr.split("&");

  paramsArray.forEach((param) => {
    const [key, value] = param.split("=");
    result[key] = decodeURIComponent(value);
  });
  return result;
}

// prettier-ignore
export function calcTicketSubtotal(cartState: CartManagerState, shouldSumPerks = false, shouldSumOrderServiceFee = false) {
  let subtotal = 0;
  for (let itm of cartState.cart) {
    if (itm?.products?.length > 0) {
      let prices = itm.products.map((prd: any) => {
        if (shouldSumPerks) subtotal += calculateInventoryPerkSubTotal(prd);
        if (prd?.allInPrice) return Number(prd?.allInPrice) * Number(prd.seatCount); // prettier-ignore
        if (prd?.totalAllInPrice) return Number(prd?.totalAllInPrice); // prettier-ignore
        if (prd?.totalPrice) return Number(prd?.totalPrice);
        return Number((prd.price) || 0) * Number(prd.seatCount);
      });
      subtotal += prices.reduce((acc, curr) => acc + curr);
    }
  }
  if (shouldSumOrderServiceFee && cartState?.orderServiceFee && cartState?.orderServiceFee > 0) subtotal += cartState?.orderServiceFee;
  return subtotal;
}

// prettier-ignore
export function calculateInventoryPerkSubTotal(inventory: SuitLevelV2Inventory) {
  let subtotal = 0;
  if (inventory?.perks?.length) {
    for (const perk of inventory.perks) {
      if (perk?.allInPrice) subtotal += perk.allInPrice * perk.seatCount;
      else if (perk?.totalAllInPrice) subtotal += perk.totalAllInPrice;
      else subtotal += (perk.basePrice + perk.serviceFee) * perk.seatCount;
    }
  }
  return subtotal;
}

// prettier-ignore
export function calculateAllCartMandatoryPerkCost(cart: CartManagerState["cart"]): number {
  let mandatoryPerkCost = 0;
  for (const cartItem of cart) {
    if (cartItem?.perks) {
      for (const product of cartItem.perks) {
        if (product?.isMandatory) {
          if (product?.allInPrice) mandatoryPerkCost += product.allInPrice * product.seatCount;
          else if (product?.totalAllInPrice) mandatoryPerkCost += product.totalAllInPrice;
          else mandatoryPerkCost += (product.basePrice + product.serviceFee) * product.seatCount;
        }
      }
    }
  }
  return mandatoryPerkCost;
}

export const calcPerksSubtotal = (cart: CartManagerState["cart"]) => {
  let subtotal = 0;
  cart.forEach((cartEvent) => {
    cartEvent?.perks?.forEach((perk) => {
      if (perk?.allInPrice) subtotal += perk.allInPrice * (perk.seatCount || 0);
      else if (perk?.totalAllInPrice) subtotal += perk.totalAllInPrice;
      else subtotal += perk.basePrice * (perk.seatCount || 0);
    });
  });
  return subtotal;
};

// prettier-ignore
export function calculateAllCartNonMandatoryPerkCost(cart: CartManagerState["cart"]) {
  return cart?.reduce((acc, cartItem) => {
    if (!cartItem?.perks?.length) return acc;
    return (acc + cartItem.perks.reduce((acc, perk) => {
      return (!perk.isMandatory && acc + (perk?.allInPrice as number) * perk.seatCount) || 0;
    }, 0));
  }, 0);
}

function clearNumber(value = "") {
  return value.replace(/\D+/g, "");
}

export function formatCreditCardNumber(value: string) {
  if (!value) {
    return value;
  }

  const issuer = Payment.fns.cardType(value);
  const clearValue = clearNumber(value);
  let nextValue;

  switch (issuer) {
    case "amex":
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10
      )} ${clearValue.slice(10, 15)}`;
      break;
    case "dinersclub":
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        10
      )} ${clearValue.slice(10, 14)}`;
      break;
    default:
      nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
        4,
        8
      )} ${clearValue.slice(8, 12)} ${clearValue.slice(12, 16)}`;
      break;
  }

  return nextValue.trim();
}

export function formatExpirationDate(event: any) {
  const eventData = event.nativeEvent && event.nativeEvent.data;
  const prevExpiry = event.target.value.split(" / ").join("/");

  if (!prevExpiry) return null;
  let expiry = prevExpiry;
  if (/^[2-9]$/.test(expiry)) {
    expiry = `0${expiry}`;
  }

  if (prevExpiry.length === 2 && +prevExpiry > 12) {
    const [head, ...tail] = prevExpiry.split("");
    expiry = `0${head}/${tail.join("")}`;
  }

  if (/^1[/-]$/.test(expiry)) {
    return `01 / `;
  }

  expiry = expiry.match(/(\d{1,2})/g) || [];
  if (expiry.length === 1) {
    if (!eventData && prevExpiry.includes("/")) {
      return expiry[0];
    }
    if (/\d{2}/.test(expiry)) {
      return `${expiry[0]} / `;
    }
  }

  if (expiry.length > 2) {
    const [, month = null, year = null] =
      expiry.join("").match(/^(\d{2}).*(\d{2})$/) || [];
    return [month, year].join(" / ");
  }

  return expiry.join(" / ");
}

export function formatCVV(value: string) {
  return value.replace(/\D/g, "").slice(0, 4);
}

export function createUrlFromNextRouter(route: string, teamConfig: TeamConfig) {
  const { teamSchema } = teamConfig;
  const { frontendUrl } = globalConfig;
  let path = route.replace("/[teamSchema]", "");
  return `${frontendUrl}/${teamSchema}/${path}`;
}

export function objectToParamString(obj?: Record<string, any>) {
  if (!obj || obj?.keys?.length === 0) return "";
  return (
    "?" +
    Object.keys(obj)
      .map((key) => key + "=" + obj[key])
      .join("&")
  );
}

// prettier-ignore
function normalizeZipCode(zipCode: string | undefined): string {
  if (!zipCode) return "";

  const normalizeNumericZipCode = (code: string) => code.split("-")[0].replace(/[^0-9]/g, "");
  const normalizeAlphanumericZipCode = (code: string) => code.replace(/[^A-Z0-9]/gi, "").toLowerCase();

  const isNumericZipCode = /^[0-9\-]+$/.test(zipCode);

  let normalizedZipCode: string;

  if (isNumericZipCode) {
    normalizedZipCode = normalizeNumericZipCode(zipCode);
    normalizedZipCode = normalizedZipCode.slice(0, 5);
  } else {
    normalizedZipCode = normalizeAlphanumericZipCode(zipCode);
  }

  return normalizedZipCode;
}

export function verifyZipCode(
  zipCode: string,
  allowList: string[],
  denyList: string[]
) {
  const normalizedZipCode = normalizeZipCode(zipCode);

  let isVerified = true;
  if (allowList.length > 0) {
    isVerified = allowList.some((zip) => zip == normalizedZipCode);
  }
  if (denyList.length > 0) {
    isVerified = !denyList.some((zip) => zip == normalizedZipCode);
  }
  return isVerified;
}

export function throttleTime(delay: number, callback?: () => void) {
  return new Promise<void>((resolve, reject) => {
    let timeout = setTimeout(() => {
      callback?.();
      resolve();
      clearTimeout(timeout);
    }, delay);
  });
}

export function pluralize(noun: string, count: number) {
  if (count == 1) {
    return noun;
  } else {
    // Check for special cases where "es" is needed
    if (
      noun.endsWith("s") ||
      noun.endsWith("sh") ||
      noun.endsWith("ch") ||
      noun.endsWith("x") ||
      noun.endsWith("z")
    ) {
      return noun + "es";
    } else {
      return noun + "s";
    }
  }
}

// prettier-ignore
export function formatDate(date: string | undefined, displayYear = false, displayFullMonth = false) {
  if (!date) return;

  const [month, day, year] = date.includes("-")
    ? date.split("-").map(Number)
    : date.split("/").map(Number);

  const parsedDate = new Date(year, month - 1, day);
  return format(parsedDate, displayYear ? "MMM do yyyy" : displayFullMonth ? "MMMM do" : "MMM do");
}

export function passwordRegExp() {
  return /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/g;
}

export function handleFormatPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return "";
  return phoneNumber.replace(/\+1|\s|-/g, "");
}

export function openPdfInNewTab(pdfUrl: string) {
  const newWindow = window.open(pdfUrl, "_blank");
  if (newWindow) newWindow.opener = null;
}

export async function getSHA256Hash(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export function formatUSPhoneNumberWithBracket(input?: string) {
  if (!input) return "";

  const formatted = `(${input.slice(0, 3)}) ${input.slice(
    3,
    6
  )} - ${input.slice(6)}`;

  return formatted;
}

export function formatUSPhoneNumber(input?: string) {
  if (!input) return "";
  // Remove any non-digit characters from the input
  let digits = input.replace(/\D/g, "");

  // Ensure the number is no longer than 10 digits
  if (digits.length > 10) {
    digits = digits.slice(0, 10);
  }

  // Format the phone number
  let formatted = digits;
  if (digits.length > 3) {
    formatted = digits.slice(0, 3) + "-" + digits.slice(3);
  }
  if (digits.length > 6) {
    formatted = formatted.slice(0, 7) + "-" + formatted.slice(7);
  }

  return formatted;
}
